import { AranacaklarQuickFilter } from './AranacaklarQuickFilter.jsx';
import { FilterCheckboxList, FilterRange, FilterSelect } from './PortalFilterFields.jsx';
import { PortalListingAttrFilters } from './PortalListingAttrFilters.jsx';
import {
  isLandCategoryFilters,
  metaScorePctMaxForStars,
  PORTAL_LIST_MODE,
  splitCsvParam,
} from '../lib/portal-listing-filters-build.js';
import { isWebIlanPortalUiEnabled } from '../lib/web-ilan-portal-ui.js';

const LM = PORTAL_LIST_MODE;

const NAV_CITY_MAX_OPTIONS = [
  { value: '', label: 'Tümü' },
  { value: '5000', label: '≤ 5 km' },
  { value: '10000', label: '≤ 10 km' },
  { value: '20000', label: '≤ 20 km' },
  { value: '35000', label: '≤ 35 km' },
];

const NAV_TOWN_MAX_OPTIONS = [
  { value: '', label: 'Tümü' },
  { value: '3000', label: '≤ 3 km' },
  { value: '8000', label: '≤ 8 km' },
  { value: '15000', label: '≤ 15 km' },
];

const LAND_INFRA_TRI_OPTIONS = [
  { value: '', label: 'Tümü' },
  { value: '1', label: 'Evet' },
  { value: '0', label: 'Hayır' },
];

const META_STAR_FILTER_OPTIONS = [
  { value: '', label: 'Tümü' },
  { value: '1', label: '1 yıldız ve altı' },
  { value: '2', label: '2 yıldız ve altı' },
  { value: '3', label: '3 yıldız ve altı' },
  { value: '4', label: '4 yıldız ve altı' },
  { value: '5', label: '5 yıldız ve altı' },
];

function FilterCollapse({ title, children, defaultOpen = false, bodyClassName = '' }) {
  const resolvedBodyClassName = bodyClassName
    ? `pp-portal-filter-collapse__body ${bodyClassName}`
    : 'pp-portal-filter-collapse__body';
  return (
    <details className="pp-portal-filter-collapse" open={defaultOpen}>
      <summary className="pp-portal-filter-collapse__summary">{title}</summary>
      <div className={resolvedBodyClassName}>{children}</div>
    </details>
  );
}

/**
 * @param {object} props
 * @param {boolean} [props.listingSearchOnly]
 * @param {boolean} [props.useCollapse]
 * @param {object} props.draftFilters
 * @param {(fn: (cur: object) => object) => void} props.setDraftFilters
 * @param {(field: string, value: unknown) => void} props.setFilterValue
 * @param {object} [props.appliedFilters]
 * @param {(mode: string) => void} [props.setListMode]
 * @param {(patch: object) => void} [props.applyDraftFilterPatch]
 * @param {Array<{value: string, label: string}>} props.cityOptions
 * @param {Array<{value: string, label: string}>} props.townOptions
 * @param {Array<{value: string, label: string}>} props.quarterOptions
 * @param {Array<{value: string, label: string, isLeaf?: boolean}>} props.queryTypeOptionsForDraft
 * @param {string[]} props.selectedQueryTypes
 * @param {string} props.metaStarsFilterValue
 * @param {boolean} [props.consultantFilterEnabled]
 * @param {boolean} [props.isAuthenticated]
 * @param {boolean} [props.consultantUsersLoading]
 * @param {Array<{user_id: string|number, display_name: string}>} [props.consultantUserOptions]
 * @param {(userId: string, displayName: string) => void} [props.onConsultantFilterChange]
 * @param {import('preact').ComponentChildren} [props.footerActions]
 */
export function PortalListingFiltersForm({
  listingSearchOnly = false,
  useCollapse = true,
  draftFilters,
  setDraftFilters,
  setFilterValue,
  appliedFilters = null,
  setListMode = null,
  applyDraftFilterPatch = null,
  cityOptions,
  townOptions,
  quarterOptions,
  queryTypeOptionsForDraft,
  selectedQueryTypes,
  metaStarsFilterValue,
  consultantFilterEnabled = false,
  isAuthenticated = false,
  consultantUsersLoading = false,
  consultantUserOptions = [],
  onConsultantFilterChange = null,
  footerActions = null,
}) {
  const showListMode = !listingSearchOnly && setListMode && appliedFilters;
  const showAranacaklar = !listingSearchOnly && applyDraftFilterPatch;
  const showNav = !listingSearchOnly && draftFilters.list_mode === LM.QUERIES;
  const showMine = !listingSearchOnly;

  const locationAndTypeBlock = (
    <>
      <div className="pp-filter-group-title">Konum</div>
      <FilterSelect
        label=""
        value={draftFilters.city_id}
        options={cityOptions}
        onChange={(value) => setFilterValue('city_id', value)}
      />
      <FilterSelect
        label=""
        value={draftFilters.town_id}
        options={townOptions}
        disabled={!draftFilters.city_id}
        onChange={(value) => setFilterValue('town_id', value)}
      />
      <FilterSelect
        label=""
        value={draftFilters.quarter_id}
        options={quarterOptions}
        disabled={!draftFilters.town_id}
        onChange={(value) => setFilterValue('quarter_id', value)}
      />
      <FilterCheckboxList
        key={draftFilters.category_main || 'all-query-types'}
        label="Tür"
        options={queryTypeOptionsForDraft}
        selectedValues={selectedQueryTypes}
        placeholder={draftFilters.category_main ? 'Tümü' : 'Önce kategori seçin'}
        disabled={!draftFilters.category_main}
        onSelectAll={() => setDraftFilters((cur) => ({
          ...cur,
          category_type: '',
          category_leaf_id: '',
          query_type: '',
        }))}
        onToggle={(option) => {
          setDraftFilters((cur) => {
            const nextLeaf = new Set(splitCsvParam(cur.category_leaf_id));
            const nextBranch = new Set(splitCsvParam(cur.category_type));
            const bucket = option.isLeaf ? nextLeaf : nextBranch;
            if (bucket.has(option.value)) bucket.delete(option.value);
            else bucket.add(option.value);
            return {
              ...cur,
              category_type: [...nextBranch].join(','),
              category_leaf_id: [...nextLeaf].join(','),
              query_type: '',
            };
          });
        }}
      />
    </>
  );

  const ilanPortalUiOn = isWebIlanPortalUiEnabled();
  const listModeBlock = showListMode ? (
    <div className="pp-portal-list-mode" role="group" aria-label="Sonuç türü">
      {ilanPortalUiOn ? (
        <button
          type="button"
          className={`pp-portal-list-mode-btn is-mode-listings${appliedFilters.list_mode === LM.LISTINGS ? ' is-active' : ''}`}
          onClick={() => setListMode(LM.LISTINGS)}
        >
          İlanlar
        </button>
      ) : null}
      <button
        type="button"
        className={`pp-portal-list-mode-btn is-mode-queries${appliedFilters.list_mode === LM.QUERIES ? ' is-active' : ''}`}
        onClick={() => setListMode(LM.QUERIES)}
      >
        Sorgular
      </button>
      {ilanPortalUiOn ? (
        <button
          type="button"
          className={`pp-portal-list-mode-btn is-mode-all${appliedFilters.list_mode === LM.ALL ? ' is-active' : ''}`}
          onClick={() => setListMode(LM.ALL)}
          title="Gizli ilanlar — danışman, kurumsal ve admin üyeleri"
        >
          Sandık
        </button>
      ) : null}
    </div>
  ) : null;

  const aranacaklarBlock = showAranacaklar ? (
    <AranacaklarQuickFilter
      disabled={false}
      contactsFetchEnabled
      selectedContactId={draftFilters.aranacaklar_contact_id}
      onApplyPatch={applyDraftFilterPatch}
    />
  ) : null;

  const consultantFilterBlock = consultantFilterEnabled ? (
    <>
      <div className="pp-filter-group-title">Kullanıcı Filtresi</div>
      <label className="pp-filter-field">
        <select
          value={draftFilters.consultant_user_id || ''}
          disabled={!isAuthenticated || consultantUsersLoading}
          onChange={(event) => {
            const nextId = String(event.currentTarget.value || '').trim();
            const selected = consultantUserOptions.find(
              (row) => String(row?.user_id || '') === nextId,
            );
            const nextName = selected?.display_name || '';
            if (typeof onConsultantFilterChange === 'function') {
              onConsultantFilterChange(nextId, nextName);
            }
          }}
          aria-label="Kullanıcı filtre seçimi"
        >
          <option value="">
            {consultantUsersLoading ? 'Yükleniyor…' : 'Tüm kullanıcılar'}
          </option>
          {consultantUserOptions.map((row) => (
            <option key={String(row.user_id)} value={String(row.user_id)}>
              {row.display_name}
            </option>
          ))}
        </select>
      </label>
    </>
  ) : null;

  const attrBlock = (
    <PortalListingAttrFilters
      categoryMain={draftFilters.category_main}
      categoryTypeCsv={draftFilters.category_type}
      categoryLeafIdCsv={draftFilters.category_leaf_id}
      listMode={draftFilters.list_mode}
      listingAttrJson={draftFilters.listing_attr}
      onListingAttrChange={(json) => setDraftFilters((cur) => ({ ...cur, listing_attr: json }))}
    />
  );

  const flagsBlock = (
    <>
      <label className="pp-filter-check">
        <input
          type="checkbox"
          checked={draftFilters.expert_answered === '1'}
          onChange={(event) =>
            setFilterValue('expert_answered', event.currentTarget.checked ? '1' : '')
          }
        />
        <span>Uzman Talebi</span>
      </label>
      <label className="pp-filter-check">
        <input
          type="checkbox"
          checked={draftFilters.price_advantage_only === '1'}
          onChange={(event) =>
            setFilterValue('price_advantage_only', event.currentTarget.checked ? '1' : '')
          }
        />
        <span>Sadece Fiyat Avantajı</span>
      </label>
    </>
  );

  const areaBlock = (
    <>
      <FilterRange
        label="m2 Aralığı"
        minValue={draftFilters.area_m2_min}
        maxValue={draftFilters.area_m2_max}
        minPlaceholder="Min m2"
        maxPlaceholder="Max m2"
        onMinChange={(value) => setFilterValue('area_m2_min', value)}
        onMaxChange={(value) => setFilterValue('area_m2_max', value)}
      />
      <FilterRange
        label="Yola cephe (m)"
        minValue={draftFilters.road_frontage_min_m}
        maxValue={draftFilters.road_frontage_max_m}
        minPlaceholder="Min m"
        maxPlaceholder="Max m"
        onMinChange={(value) => setFilterValue('road_frontage_min_m', value)}
        onMaxChange={(value) => setFilterValue('road_frontage_max_m', value)}
      />
    </>
  );

  const navSelects = showNav ? (
    <>
      <FilterSelect
        label="Şehir merkezi — en fazla"
        value={draftFilters.max_nav_city_m}
        options={NAV_CITY_MAX_OPTIONS}
        onChange={(value) => setFilterValue('max_nav_city_m', value)}
      />
      <FilterSelect
        label="İlçe merkezi — en fazla"
        value={draftFilters.max_nav_town_m}
        options={NAV_TOWN_MAX_OPTIONS}
        onChange={(value) => setFilterValue('max_nav_town_m', value)}
      />
    </>
  ) : null;

  const navBlockFlat = navSelects ? (
    <>
      <div className="pp-filter-group-title">Merkez rota mesafesi</div>
      {navSelects}
    </>
  ) : null;

  const priceBlock = (
    <>
      <FilterRange
        label="Birim Fiyat Aralığı"
        minValue={draftFilters.unit_price_min}
        maxValue={draftFilters.unit_price_max}
        minPlaceholder="Min birim"
        maxPlaceholder="Max birim"
        onMinChange={(value) => setFilterValue('unit_price_min', value)}
        onMaxChange={(value) => setFilterValue('unit_price_max', value)}
      />
      <FilterRange
        label="Fiyat Aralığı"
        minValue={draftFilters.total_price_min}
        maxValue={draftFilters.total_price_max}
        minPlaceholder="Min fiyat"
        maxPlaceholder="Max fiyat"
        onMinChange={(value) => setFilterValue('total_price_min', value)}
        onMaxChange={(value) => setFilterValue('total_price_max', value)}
      />
    </>
  );

  const scoresBlock = (
    <>
      <FilterSelect
        label="Genel Puan — yıldız (1–5)"
        value={metaStarsFilterValue}
        options={META_STAR_FILTER_OPTIONS}
        onChange={(value) => {
          setDraftFilters((current) => ({
            ...current,
            meta_min: '',
            meta_max: metaScorePctMaxForStars(value),
          }));
        }}
      />
      <FilterRange
        label="Mülk skoru (0–100)"
        minValue={draftFilters.gm_min}
        maxValue={draftFilters.gm_max}
        minPlaceholder="Min"
        maxPlaceholder="Max"
        onMinChange={(value) => setFilterValue('gm_min', value)}
        onMaxChange={(value) => setFilterValue('gm_max', value)}
      />
      <FilterRange
        label="Kullanıcı puanı % (0–100)"
        minValue={draftFilters.puan_min}
        maxValue={draftFilters.puan_max}
        minPlaceholder="Min %"
        maxPlaceholder="Max %"
        onMinChange={(value) => setFilterValue('puan_min', value)}
        onMaxChange={(value) => setFilterValue('puan_max', value)}
      />
    </>
  );

  const landInfraSelects = isLandCategoryFilters(draftFilters) ? (
    <>
      <FilterSelect
        label="Yol"
        value={draftFilters.listing_has_road}
        options={LAND_INFRA_TRI_OPTIONS}
        onChange={(value) => setFilterValue('listing_has_road', value)}
      />
      <FilterSelect
        label="Su"
        value={draftFilters.listing_has_water}
        options={LAND_INFRA_TRI_OPTIONS}
        onChange={(value) => setFilterValue('listing_has_water', value)}
      />
      <FilterSelect
        label="Elektrik hattı"
        value={draftFilters.listing_has_power_line}
        options={LAND_INFRA_TRI_OPTIONS}
        onChange={(value) => setFilterValue('listing_has_power_line', value)}
      />
    </>
  ) : null;

  const landInfraBlockFlat = landInfraSelects ? (
    <>
      <div className="pp-filter-group-title">Arazi ilanı — altyapı</div>
      {landInfraSelects}
    </>
  ) : null;

  const mineBlock = showMine ? (
    <label className="pp-filter-check">
      <input
        type="checkbox"
        checked={draftFilters.mine}
        onChange={(event) => setFilterValue('mine', event.currentTarget.checked)}
      />
      <span>Sadece benim sorgularım</span>
    </label>
  ) : null;

  if (!useCollapse) {
    return (
      <div className="pp-portal-filters-form">
        {listModeBlock}
        {locationAndTypeBlock}
        {aranacaklarBlock}
        {consultantFilterBlock}
        {attrBlock}
        {flagsBlock}
        {areaBlock}
        {navBlockFlat}
        {priceBlock}
        {scoresBlock}
        {landInfraBlockFlat}
        {mineBlock}
        {footerActions}
      </div>
    );
  }

  return (
    <div className="pp-portal-filters-form pp-portal-filters-form--collapse">
      {showListMode ? (
        <FilterCollapse title="Sonuç türü" defaultOpen>
          {listModeBlock}
        </FilterCollapse>
      ) : null}

      <FilterCollapse title="Konum ve kategori türü" defaultOpen>
        {locationAndTypeBlock}
      </FilterCollapse>

      {showAranacaklar || consultantFilterEnabled ? (
        <FilterCollapse
          title="Müşteri ve kullanıcı filtresi"
          defaultOpen
          bodyClassName="pp-portal-filter-collapse__body--customer"
        >
          {aranacaklarBlock}
          {consultantFilterBlock}
        </FilterCollapse>
      ) : null}

      <FilterCollapse title="İlan özellikleri (kategoriye göre)" defaultOpen>
        {attrBlock}
      </FilterCollapse>

      <FilterCollapse title="Talep ve işaretler">
        {flagsBlock}
      </FilterCollapse>

      <FilterCollapse title="Alan ve yola cephe">
        {areaBlock}
      </FilterCollapse>

      {navSelects ? (
        <FilterCollapse title="Merkez rota mesafesi (sorgular)">
          {navSelects}
        </FilterCollapse>
      ) : null}

      <FilterCollapse title="Fiyat">
        {priceBlock}
      </FilterCollapse>

      <FilterCollapse title="Skorlar ve yıldız">
        {scoresBlock}
      </FilterCollapse>

      {landInfraSelects ? (
        <FilterCollapse title="Arazi ilanı — altyapı">
          {landInfraSelects}
        </FilterCollapse>
      ) : null}

      {showMine ? (
        <FilterCollapse title="Kayıt">
          {mineBlock}
        </FilterCollapse>
      ) : null}

      {footerActions}
    </div>
  );
}
